// How far through an admin vehicle inspection someone is, and the saved "draft" that lets them carry on later.
//
// An admin inspection must cover the same things as the driver's Vehicle Check: the four exterior photos, the oil
// dipstick photo, the odometer reading (when the company requires it) and the whole checklist -- plus who did it.
// Every one of those counts equally towards the percentage. It reads 100% (and the inspection can be completed)
// only when every single one is done; until then it never shows 100%.

export interface InspectionProgressInput {
  /** Exterior photos taken, 0-4. */
  exteriorPhotos: number;
  oilDipstickPhoto: boolean;
  odometerRequired: boolean;
  odometerEntered: boolean;
  completedByEntered: boolean;
  checklistTotal: number;
  /** Checklist items the admin has actually answered. */
  checklistConfirmed: number;
}

export interface InspectionProgress {
  done: number;
  total: number;
  /** 0-100. Only 100 when `complete`. */
  pct: number;
  complete: boolean;
  /** What is still to do, in plain words, e.g. ["2 exterior photos", "Oil dipstick photo", "5 checklist items"]. */
  missing: string[];
}

export const EXTERIOR_PHOTO_COUNT = 4;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function computeInspectionProgress(i: InspectionProgressInput): InspectionProgress {
  const exterior = Math.max(0, Math.min(EXTERIOR_PHOTO_COUNT, i.exteriorPhotos));
  const checklistTotal = Math.max(0, i.checklistTotal);
  const checklistDone = Math.max(0, Math.min(checklistTotal, i.checklistConfirmed));

  const total = EXTERIOR_PHOTO_COUNT + 1 + (i.odometerRequired ? 1 : 0) + 1 + checklistTotal;
  const done =
    exterior +
    (i.oilDipstickPhoto ? 1 : 0) +
    (i.odometerRequired && i.odometerEntered ? 1 : 0) +
    (i.completedByEntered ? 1 : 0) +
    checklistDone;

  const missing: string[] = [];
  if (exterior < EXTERIOR_PHOTO_COUNT) missing.push(plural(EXTERIOR_PHOTO_COUNT - exterior, "exterior photo", "exterior photos"));
  if (!i.oilDipstickPhoto) missing.push("Oil dipstick photo");
  if (i.odometerRequired && !i.odometerEntered) missing.push("Odometer reading");
  if (!i.completedByEntered) missing.push("Completed by");
  if (checklistDone < checklistTotal) missing.push(plural(checklistTotal - checklistDone, "checklist item", "checklist items"));

  const complete = done === total;
  return { done, total, complete, pct: complete ? 100 : Math.min(99, Math.floor((done / total) * 100)), missing };
}

// ---------------------------------------------------------------------------
// Draft (saved progress)
// ---------------------------------------------------------------------------

export interface DraftItemAnswer {
  status: string;
  note: string;
  confirmed: boolean;
}

export interface InspectionDraft {
  completedBy: string;
  odometer: string;
  notes: string;
  /** Answers by checklist item_key. */
  items: Record<string, DraftItemAnswer>;
  /** Damage markers by photo type (opaque here; the form owns their shape). */
  damageMarkers?: Record<string, unknown>;
}

/** Drafts older than this are ignored: a fresh inspection starts instead. */
export const DRAFT_MAX_AGE_DAYS = 30;

export const isDraftFresh = (createdAtIso: string, now: Date = new Date()): boolean =>
  now.getTime() - new Date(createdAtIso).getTime() <= DRAFT_MAX_AGE_DAYS * 86_400_000;

interface ChecklistLike {
  item_key: string;
  status: string;
  note: string;
  confirmed?: boolean;
}

/** The checklist with a saved draft's answers laid back on. Items the draft doesn't know about stay unanswered. */
export function applyDraftToChecklist<T extends ChecklistLike>(items: T[], draft: InspectionDraft | null | undefined): T[] {
  if (!draft?.items) return items;
  return items.map((item) => {
    const saved = draft.items[item.item_key];
    return saved ? { ...item, status: saved.status, note: saved.note, confirmed: !!saved.confirmed } : item;
  });
}

/** Only answered items (or ones with a note) need saving; the rest are simply "not done yet". */
export function buildDraftItems<T extends ChecklistLike>(items: T[]): Record<string, DraftItemAnswer> {
  const out: Record<string, DraftItemAnswer> = {};
  for (const item of items) {
    if (item.confirmed || item.note) out[item.item_key] = { status: item.status, note: item.note, confirmed: !!item.confirmed };
  }
  return out;
}
